import React from "react";

/**
 * Renders message text with basic markdown-style formatting.
 */
const MessageText = ({ text, isSent = false }) => {
  if (!text) return null;

  const linkColor = isSent ? "#cce0ff" : "#025FEB";
  const codeBg = isSent ? "rgba(255,255,255,0.15)" : "rgba(0,0,0,0.06)";
  const quoteBorder = isSent ? "rgba(255,255,255,0.4)" : "#ccc";

  const parseInline = (str, keyPrefix = "") => {
    const parts = [];
    const regex =
      /(\{\{f:([^}|]+)\|(\d+)\}\}([\s\S]*?)\{\{\/f\}\}|\{\{c:(#[0-9a-fA-F]{3,8})\}\}([\s\S]*?)\{\{\/c\}\}|\{\{h:(#[0-9a-fA-F]{3,8})\}\}([\s\S]*?)\{\{\/h\}\}|\{\{a:(left|center|right)\}\}([\s\S]*?)\{\{\/a\}\}|\{\{lh:([\d.]+)\}\}([\s\S]*?)\{\{\/lh\}\}|\*\*\*(.+?)\*\*\*|\*\*(.+?)\*\*|__(.+?)__|\*(.+?)\*|~~(.+?)~~|`([^`]+)`|\[([^\]]+)\]\(([^)]+)\))/g;
    let lastIndex = 0;
    let match;
    let i = 0;

    while ((match = regex.exec(str)) !== null) {
      if (match.index > lastIndex) {
        parts.push(str.slice(lastIndex, match.index));
      }

      const key = `${keyPrefix}-${i++}`;
      if (match[2] && match[3] && match[4] != null) {
        parts.push(
          <span
            key={key}
            style={{ fontFamily: match[2], fontSize: `${match[3]}px` }}
          >
            {parseInline(match[4], `${key}-f`)}
          </span>
        );
      } else if (match[5] && match[6] != null) {
        parts.push(
          <span key={key} style={{ color: match[5] }}>
            {parseInline(match[6], `${key}-c`)}
          </span>
        );
      } else if (match[7] && match[8] != null) {
        parts.push(
          <span key={key} style={{ backgroundColor: match[7], borderRadius: "2px", padding: "0 2px" }}>
            {parseInline(match[8], `${key}-h`)}
          </span>
        );
      } else if (match[9] && match[10] != null) {
        parts.push(
          <span key={key} style={{ display: "block", textAlign: match[9] }}>
            {parseInline(match[10], `${key}-a`)}
          </span>
        );
      } else if (match[11] && match[12] != null) {
        parts.push(
          <span key={key} style={{ display: "block", lineHeight: match[11] }}>
            {parseInline(match[12], `${key}-lh`)}
          </span>
        );
      } else if (match[13]) {
        parts.push(
          <strong key={key}>
            <em>{parseInline(match[13], `${key}-bi`)}</em>
          </strong>
        );
      } else if (match[14]) {
        parts.push(<strong key={key}>{parseInline(match[14], `${key}-b`)}</strong>);
      } else if (match[15]) {
        parts.push(<u key={key}>{parseInline(match[15], `${key}-u`)}</u>);
      } else if (match[16]) {
        parts.push(<em key={key}>{parseInline(match[16], `${key}-i`)}</em>);
      } else if (match[17]) {
        parts.push(<s key={key}>{parseInline(match[17], `${key}-s`)}</s>);
      } else if (match[18]) {
        parts.push(
          <code
            key={key}
            style={{
              backgroundColor: codeBg,
              padding: "1px 5px",
              borderRadius: "4px",
              fontSize: "0.9em",
              fontFamily: "monospace",
            }}
          >
            {match[18]}
          </code>
        );
      } else if (match[19] && match[20]) {
        parts.push(
          <a
            key={key}
            href={match[20]}
            target="_blank"
            rel="noopener noreferrer"
            style={{ color: linkColor, textDecoration: "underline" }}
          >
            {match[19]}
          </a>
        );
      }

      lastIndex = regex.lastIndex;
    }

    if (lastIndex < str.length) {
      parts.push(str.slice(lastIndex));
    }

    return parts.length ? parts : [str];
  };

  const getIndentPadding = (line) => {
    const match = line.match(/^(\s+)/);
    if (!match) return 0;
    const spaces = match[1].replace(/\t/g, "  ").length;
    return Math.floor(spaces / 2) * 16;
  };

  // Block-level formatting (font/color/highlight/align) can wrap text that
  // spans multiple lines, e.g. "{{f:Arial|24}}a\nb{{/f}}". Since we parse
  // line-by-line below, redistribute such wrappers so each line carries its
  // own self-contained marker and renders instead of showing raw markup.
  const expandMultilineMarkup = (input) => {
    const patterns = [
      { re: /\{\{f:([^}|]+)\|(\d+)\}\}([\s\S]*?)\{\{\/f\}\}/, open: (m) => `{{f:${m[1]}|${m[2]}}}`, close: "{{/f}}", body: 3 },
      { re: /\{\{c:(#[0-9a-fA-F]{3,8})\}\}([\s\S]*?)\{\{\/c\}\}/, open: (m) => `{{c:${m[1]}}}`, close: "{{/c}}", body: 2 },
      { re: /\{\{h:(#[0-9a-fA-F]{3,8})\}\}([\s\S]*?)\{\{\/h\}\}/, open: (m) => `{{h:${m[1]}}}`, close: "{{/h}}", body: 2 },
      { re: /\{\{a:(left|center|right)\}\}([\s\S]*?)\{\{\/a\}\}/, open: (m) => `{{a:${m[1]}}}`, close: "{{/a}}", body: 2 },
      { re: /\{\{lh:([\d.]+)\}\}([\s\S]*?)\{\{\/lh\}\}/, open: (m) => `{{lh:${m[1]}}}`, close: "{{/lh}}", body: 2 },
    ];
    let result = input;
    let guard = 0;
    let changed = true;
    while (changed && guard++ < 100) {
      changed = false;
      for (const p of patterns) {
        const m = p.re.exec(result);
        if (m && m[p.body].includes("\n")) {
          const open = p.open(m);
          const wrapped = m[p.body]
            .split("\n")
            .map((ln) => (ln === "" ? ln : `${open}${ln}${p.close}`))
            .join("\n");
          result = result.slice(0, m.index) + wrapped + result.slice(m.index + m[0].length);
          changed = true;
          break;
        }
      }
    }
    return result;
  };

  const lines = expandMultilineMarkup(text).split("\n");
  const elements = [];
  let inCodeBlock = false;
  let codeBlockLines = [];
  let codeBlockKey = 0;

  const flushCodeBlock = () => {
    if (codeBlockLines.length) {
      elements.push(
        <pre
          key={`code-${codeBlockKey++}`}
          style={{
            backgroundColor: codeBg,
            padding: "8px 10px",
            borderRadius: "6px",
            fontSize: "0.9em",
            fontFamily: "monospace",
            margin: "4px 0",
            overflowX: "auto",
            whiteSpace: "pre-wrap",
          }}
        >
          <code>{codeBlockLines.join("\n")}</code>
        </pre>
      );
      codeBlockLines = [];
    }
  };

  lines.forEach((line, idx) => {
    if (line.trim().startsWith("```")) {
      if (inCodeBlock) {
        inCodeBlock = false;
        flushCodeBlock();
      } else {
        flushCodeBlock();
        inCodeBlock = true;
      }
      return;
    }

    if (inCodeBlock) {
      codeBlockLines.push(line);
      return;
    }

    const indentPad = getIndentPadding(line);
    const contentLine = line.replace(/^\s+/, "");
    const indentStyle = indentPad ? { paddingLeft: `${indentPad}px` } : undefined;

    const blockquoteMatch = contentLine.match(/^>\s?(.*)/);
    if (blockquoteMatch) {
      elements.push(
        <blockquote
          key={`bq-${idx}`}
          style={{
            borderLeft: `3px solid ${quoteBorder}`,
            margin: "4px 0",
            paddingLeft: "10px",
            opacity: 0.95,
            ...indentStyle,
          }}
        >
          {parseInline(blockquoteMatch[1], `bq-${idx}`)}
        </blockquote>
      );
      return;
    }

    const bulletMatch = contentLine.match(/^[-*]\s+(.*)/);
    if (bulletMatch) {
      elements.push(
        <div
          key={`ul-${idx}`}
          style={{ display: "flex", gap: "6px", margin: "2px 0", ...indentStyle }}
        >
          <span>•</span>
          <span>{parseInline(bulletMatch[1], `ul-${idx}`)}</span>
        </div>
      );
      return;
    }

    const orderedMatch = contentLine.match(/^\d+\.\s+(.*)/);
    if (orderedMatch) {
      const num = contentLine.match(/^(\d+)\./)?.[1] || "1";
      elements.push(
        <div
          key={`ol-${idx}`}
          style={{ display: "flex", gap: "6px", margin: "2px 0", ...indentStyle }}
        >
          <span style={{ minWidth: "1.2em" }}>{num}.</span>
          <span>{parseInline(orderedMatch[1], `ol-${idx}`)}</span>
        </div>
      );
      return;
    }

    if (line.trim() === "" && idx < lines.length - 1) {
      elements.push(<br key={`br-${idx}`} />);
      return;
    }

    if (line.trim() !== "" || idx === 0) {
      elements.push(
        <span key={`line-${idx}`} style={indentStyle}>
          {parseInline(contentLine, `line-${idx}`)}
          {idx < lines.length - 1 &&
            !contentLine.match(/^[-*]\s|^\d+\.\s|^>\s?|^```/) &&
            !lines[idx + 1].trim().match(/^[-*]\s|^\d+\.\s|^>\s?|^```/) && <br />}
        </span>
      );
    }
  });

  flushCodeBlock();

  return (
    <span
      className="message-text-formatted"
      style={{
        display: "block",
        minWidth: 0,
        overflowWrap: "anywhere",
        wordBreak: "break-word",
      }}
    >
      {elements}
    </span>
  );
};

export default MessageText;
